import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, type SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import { useEffect, useId, useRef, useState } from 'react'
import { companiesQueryOptions } from '@/entities/company'
import {
  activeMeetingsQueryOptions,
  CustomerVisibleBadge,
  type MeetingListFilter,
  MeetingRefs,
  MeetingRoster,
  MeetingStateBadge,
  type MeetingSummary,
  meetingsInfiniteQueryOptions,
  rosterCountLabel,
} from '@/entities/meeting'
import { projectsQueryOptions } from '@/entities/project'
import { useRequireSession } from '@/entities/session'
import { CreateMeetingDialog } from '@/features/create-meeting'
import {
  cn,
  formatDurationBetween,
  formatElapsedSince,
  formatMeetingStart,
  formatTime,
  formatTimeNear,
  zoneHint,
} from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Field } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { useAnchoredPopover } from '@/shared/ui/popover'
import { Select } from '@/shared/ui/select'
import { Spinner } from '@/shared/ui/spinner'
import { ZoneNote } from '@/shared/ui/zone-note'

const FILTER_TABS = [
  { value: 'scheduled', label: 'Запланированные', empty: 'Запланированных встреч нет' },
  { value: 'ended', label: 'Завершённые', empty: 'Завершённых встреч нет' },
] as const satisfies readonly { value: MeetingListFilter; label: string; empty: string }[]

type TabFilter = (typeof FILTER_TABS)[number]['value']

interface MeetingsSearch {
  filter?: TabFilter
  q?: string
  tag?: string
  company?: string
  project?: string
  // Только открытые клиенту компании.
  customerVisible?: true
}

export const Route = createFileRoute('/meetings')({
  // SearchSchemaInput: ссылки на /meetings без search валидны. Дефолтный таб
  // (scheduled) держим вне URL — ?filter= появляется только для не-дефолтного.
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): MeetingsSearch => ({
    ...(search.filter === 'ended' ? { filter: 'ended' as const } : {}),
    ...(typeof search.q === 'string' && search.q !== '' ? { q: search.q } : {}),
    ...(typeof search.tag === 'string' && search.tag !== '' ? { tag: search.tag } : {}),
    ...(typeof search.company === 'string' && search.company !== ''
      ? { company: search.company }
      : {}),
    ...(typeof search.project === 'string' && search.project !== ''
      ? { project: search.project }
      : {}),
    ...(search.customerVisible === true || search.customerVisible === 'true'
      ? { customerVisible: true as const }
      : {}),
  }),
  head: () => ({ meta: [{ title: 'Встречи · STIT Телемост' }] }),
  component: MeetingsPage,
})

function MeetingsPage() {
  const navigate = useNavigate()
  useRequireSession()
  const [creating, setCreating] = useState(false)
  const search = Route.useSearch()
  const filter = search.filter ?? 'scheduled'
  const q = search.q ?? ''
  const tag = search.tag
  const company = search.company
  const project = search.project
  const customerVisible = search.customerVisible

  // Режим поиска (текст/тег/привязка к хабу/видимость клиенту) — сквозной по всем
  // состояниям встреч (filter=all), вкладки и секция «Идут сейчас» уступают место
  // результатам.
  const searching =
    q !== '' ||
    tag !== undefined ||
    company !== undefined ||
    project !== undefined ||
    customerVisible !== undefined

  // Идущие сейчас — отдельный запрос, видны поверх любого фильтра (вне поиска).
  const ongoingQuery = useQuery(activeMeetingsQueryOptions())
  const listQuery = useInfiniteQuery(
    meetingsInfiniteQueryOptions(searching ? 'all' : filter, {
      q: q || undefined,
      tag,
      company,
      project,
      customerVisible,
    }),
  )

  const ongoing = searching ? [] : (ongoingQuery.data ?? [])
  const rest = listQuery.data?.pages.flatMap((p) => p.items) ?? []
  const isLoading = ongoingQuery.isLoading || listQuery.isLoading
  const isError = ongoingQuery.isError || listQuery.isError
  const tab = FILTER_TABS.find((t) => t.value === filter) ?? FILTER_TABS[0]

  const setFilter = (value: TabFilter) => {
    void navigate({ to: '/meetings', search: value === 'scheduled' ? {} : { filter: value } })
  }

  const setTagFilter = (value: string | undefined) => {
    void navigate({
      to: '/meetings',
      search: (prev: MeetingsSearch) => ({ ...prev, tag: value }),
    })
  }

  const setCompanyFilter = (value: string | undefined) => {
    void navigate({
      // Сброс компании снимает и фильтр по проекту (проект без компании нелогичен).
      to: '/meetings',
      search: (prev: MeetingsSearch) => ({
        ...prev,
        company: value,
        ...(value ? {} : { project: undefined }),
      }),
    })
  }

  const setProjectFilter = (value: string | undefined) => {
    void navigate({
      to: '/meetings',
      search: (prev: MeetingsSearch) => ({ ...prev, project: value }),
    })
  }

  const setCustomerVisibleFilter = (value: boolean) => {
    void navigate({
      to: '/meetings',
      search: (prev: MeetingsSearch) => ({
        ...prev,
        ...(value ? { customerVisible: true as const } : { customerVisible: undefined }),
      }),
    })
  }

  // Счётчик на кнопке считает и тег: он приходит кликом по карточке, но сужает
  // выдачу наравне с остальными — иначе «Фильтры» врут о состоянии списка.
  const activeFilterCount = [tag, company, project, customerVisible].filter(
    (v) => v !== undefined,
  ).length

  const resetFilters = () => {
    void navigate({
      // Таб (запланированные/завершённые) — не фильтр выдачи, переживает сброс.
      to: '/meetings',
      search: (prev: MeetingsSearch) => (prev.filter ? { filter: prev.filter } : {}),
    })
  }

  // Имена активных фильтров — из тех же справочников, что питают селекты (кэш
  // общий). Пока справочник не доехал, показываем id: чип не должен пустовать.
  const companyDirectory = useQuery(companiesQueryOptions())
  const projectDirectory = useQuery({
    ...projectsQueryOptions(company),
    enabled: company !== undefined,
  })
  const companyName = companyDirectory.data?.find((c) => c.id === company)?.name ?? company
  const projectName = projectDirectory.data?.find((p) => p.id === project)?.name ?? project

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader
        title="Встречи команды"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} />
            Создать
          </Button>
        }
      />

      {/* Поиск живёт в шапке (⌘K-палитра); здесь — кнопка фильтров по хабу и
          чипы активных рядом с ней (тег приходит кликом по карточке). */}
      <div className="mt-15 flex flex-wrap items-center gap-2">
        <MeetingFilters
          company={company}
          project={project}
          customerVisible={customerVisible}
          activeCount={activeFilterCount}
          onCompany={setCompanyFilter}
          onProject={setProjectFilter}
          onCustomerVisible={setCustomerVisibleFilter}
          onReset={resetFilters}
        />
        {tag && (
          <FilterChip
            label={`Тег: ${tag}`}
            clearLabel={`Снять фильтр по тегу ${tag}`}
            onClear={() => setTagFilter(undefined)}
          />
        )}
        {company && (
          <FilterChip
            label={`Компания: ${companyName}`}
            clearLabel={`Снять фильтр по компании ${companyName}`}
            onClear={() => setCompanyFilter(undefined)}
          />
        )}
        {project && (
          <FilterChip
            label={`Проект: ${projectName}`}
            clearLabel={`Снять фильтр по проекту ${projectName}`}
            onClear={() => setProjectFilter(undefined)}
          />
        )}
        {customerVisible && (
          <FilterChip
            label="Видно заказчику"
            clearLabel="Снять фильтр «видно заказчику»"
            onClear={() => setCustomerVisibleFilter(false)}
          />
        )}
      </div>

      {isLoading && <Spinner label="Загрузка встреч" />}

      {isError && (
        <p className="text-accent">Не удалось загрузить встречи. Войдите в систему и повторите.</p>
      )}

      {!isLoading && !isError && ongoing.length > 0 && (
        <section className="mt-8 flex flex-col gap-3">
          {/* Зона подписана один раз на секцию: в каждой строке тег «Москва»
              превратился бы в частокол, а вопрос «в какой зоне» у колонки один. */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 className="t-h3">Идут сейчас</h2>
            <ZoneNote label="время" />
          </div>
          {/* Одна колонка (линейная лента); явный grid-cols-1 обязателен —
              implicit-колонка сайзится по max-content и выталкивает карточки
              за вьюпорт на мобиле. */}
          <ul className="grid grid-cols-1 gap-3">
            {ongoing.map((m) => {
              // startedAt появился позже createdAt — у старых встреч фолбэк.
              const startedAt = m.startedAt ?? m.createdAt
              // Длительность — по часам клиента: отставшие часы дают «меньше
              // минуты», а не пустоту вместо цифры.
              const runningFor = formatElapsedSince(startedAt)
              const startedTime = formatTime(startedAt)
              return (
                <li key={m.id}>
                  <Card>
                    <div className="flex items-start justify-between gap-3">
                      <Link to="/meetings/$id" params={{ id: m.id }} className="min-w-0 flex-1">
                        {/* mb-[1lh]: отступ до мета-строки не меньше интерлиньяжа. */}
                        <p className="t-h3 mb-[1lh] flex items-center gap-2 text-text-primary">
                          <span className="truncate">{m.title}</span>
                          {m.locked && <LockedMark />}
                          {m.private && <PrivateMark />}
                        </p>
                        <p title={zoneHint()} className="text-sm text-text-secondary">
                          {rosterCountLabel(m.roster)} · идёт
                          {runningFor ? `\u00A0${runningFor}` : ''}
                          {startedTime ? ` · с\u00A0${startedTime}` : ''}
                        </p>
                        {/* Кто в звонке прямо сейчас — до открытия карточки. */}
                        <MeetingRoster roster={m.roster} />
                      </Link>
                      <Button
                        size="sm"
                        onClick={() => void navigate({ to: '/join/$id', params: { id: m.id } })}
                      >
                        Войти
                      </Button>
                    </div>
                    {m.customerVisible && (
                      <div className="mt-2">
                        <CustomerVisibleBadge />
                      </div>
                    )}
                    <MeetingRefs
                      meeting={m}
                      onCompany={setCompanyFilter}
                      onProject={setProjectFilter}
                      className="mt-3"
                    />
                    <MeetingTags tags={m.tags} onSelect={setTagFilter} />
                  </Card>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {!isLoading && !isError && (
        // gap-5: от табов до карточек дальше, чем между карточками (gap-3),
        // но ближе, чем от инпута до табов (mt-15, 60px по макету).
        <section className="mt-15 flex flex-col gap-5">
          {/* Зона ленты подписана рядом с табами — заголовком колонки времени
              для всех карточек ниже, а не тегом в каждой строке. */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
            {searching ? (
              <p className="label">Результаты по всем встречам</p>
            ) : (
              // Табы текстом с подчёркиванием: заливка спорила с primary-кнопкой,
              // а активный таб казался выше неактивного из-за яркого фона.
              <nav aria-label="Фильтр встреч" className="flex gap-5">
                {FILTER_TABS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    aria-pressed={t.value === filter}
                    onClick={() => setFilter(t.value)}
                    className={cn(
                      'relative pb-2 font-mono text-xs font-medium uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
                      t.value === filter
                        ? 'text-text-primary after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-accent'
                        : 'text-text-tertiary hover:text-text-primary',
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </nav>
            )}
            <ZoneNote label="время" />
          </div>

          {rest.length === 0 && ongoing.length === 0 && (
            <EmptyState
              title={searching ? 'Ничего не нашлось' : tab.empty}
              description={
                searching
                  ? 'Попробуйте другой запрос или снимите фильтр по тегу.'
                  : 'Создайте первую видеовстречу.'
              }
            />
          )}
          {rest.length === 0 && ongoing.length > 0 && <p className="label mt-2">{tab.empty}</p>}

          {rest.length > 0 && (
            <>
              {/* Одна колонка (линейная лента); явный grid-cols-1 обязателен —
                  implicit-колонка сайзится по max-content и выталкивает карточки
                  за вьюпорт на мобиле. */}
              <ul className="grid grid-cols-1 gap-3">
                {rest.map((m) => {
                  // Завершённые — фактический интервал «начало — конец · длительность»
                  // (как на деталях: конец — только время, дату несёт начало);
                  // startedAt появился позже createdAt — у старых фолбэк.
                  // Остальные — запланированное время начала (ad-hoc без
                  // scheduledAt — временем создания).
                  let timeText: string | undefined
                  if (m.state === 'ended' && m.endedAt) {
                    const startedAt = m.startedAt ?? m.createdAt
                    const duration = formatDurationBetween(startedAt, m.endedAt)
                    // У запланированных — рядом плановое время: видно, во сколько
                    // договаривались и во сколько реально началось.
                    const planned = m.scheduledAt
                      ? ` · запланирована на ${formatTimeNear(m.scheduledAt, startedAt)}`
                      : ''
                    timeText = `${formatMeetingStart(startedAt)} — ${formatTime(m.endedAt)}${duration ? ` · ${duration}` : ''}${planned}`
                  } else {
                    const planned = formatMeetingStart(m.scheduledAt ?? m.createdAt)
                    if (planned)
                      timeText = m.scheduledAt
                        ? `запланирована на ${planned}`
                        : `создана ${planned}`
                  }
                  return (
                    <li key={m.id}>
                      <Card interactive>
                        <div className="flex items-start justify-between gap-3">
                          <Link to="/meetings/$id" params={{ id: m.id }} className="min-w-0 flex-1">
                            {/* mb-[1lh]: отступ до мета-строки не меньше интерлиньяжа. */}
                            <p className="t-h3 mb-[1lh] flex items-center gap-2 text-text-primary">
                              <span className="truncate">{m.title}</span>
                              {m.locked && <LockedMark />}
                              {m.private && <PrivateMark />}
                            </p>
                            <p className="text-sm text-text-secondary">
                              {rosterCountLabel(m.roster)}
                              {/* Роль смотрящего: организатор/участник — информация,
                                  «просмотр» у остальных — шум, не показываем. */}
                              {m.role === 'organizer'
                                ? ' · вы организатор'
                                : m.role === 'participant'
                                  ? ' · вы участник'
                                  : ''}
                            </p>
                            {/* Состав: кто был на встрече или кого позвали. */}
                            <MeetingRoster roster={m.roster} />
                            {timeText && (
                              <p title={zoneHint()} className="mt-1 text-sm text-text-secondary">
                                {timeText}
                              </p>
                            )}
                          </Link>
                          {/* mt-[5px]: верх бейджа на уровне верха букв заголовка
                              (кегельная площадка t-h3 начинается ниже бокса). */}
                          <MeetingStateBadge state={m.state} className="mt-[5px]" />
                        </div>
                        {/* Метки состояния и «почему встреча в выдаче»: открытость
                            клиенту обязана читаться без открытия карточки, а
                            совпадение по транскрипту в самой карточке не видно. */}
                        {(m.customerVisible || m.matchSources.includes('transcript')) && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {m.customerVisible && <CustomerVisibleBadge />}
                            {m.matchSources.includes('transcript') && (
                              <Chip variant="soft">
                                <Icon name="search" size={12} />
                                Найдено в транскрипте
                              </Chip>
                            )}
                          </div>
                        )}
                        <MeetingRefs
                          meeting={m}
                          onCompany={setCompanyFilter}
                          onProject={setProjectFilter}
                          className="mt-3"
                        />
                        <MeetingTags tags={m.tags} onSelect={setTagFilter} />
                      </Card>
                    </li>
                  )
                })}
              </ul>
              {listQuery.hasNextPage && (
                <div className="mt-2 flex justify-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={listQuery.isFetchingNextPage}
                    onClick={() => void listQuery.fetchNextPage()}
                  >
                    {listQuery.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      <CreateMeetingDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(meeting) => {
          setCreating(false)
          // Мгновенная встреча (без даты) — сразу в pre-join лобби → звонок;
          // запланированная — на страницу деталей.
          void navigate(
            meeting.scheduledAt
              ? { to: '/meetings/$id', params: { id: meeting.id } }
              : { to: '/join/$id', params: { id: meeting.id } },
          )
        }}
      />
    </div>
  )
}

// Карточка компактная: первые 5 тегов + «+N» (полный набор — в деталях встречи).
const CARD_TAG_LIMIT = 5

// Теги на карточке; клик — фильтр списка по тегу. Кнопки лежат рядом с Link
// (не внутри) — вложенный интерактив в <a> невалиден.
function MeetingTags({
  tags,
  onSelect,
}: {
  tags: MeetingSummary['tags']
  onSelect: (tag: string) => void
}) {
  if (tags.length === 0) return null
  const visible = tags.slice(0, CARD_TAG_LIMIT)
  const hidden = tags.length - visible.length
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      {visible.map((t) => (
        <button
          key={t}
          type="button"
          aria-label={`Показать встречи с тегом ${t}`}
          onClick={() => onSelect(t)}
        >
          <Chip
            variant="soft"
            className="transition-colors hover:bg-bg-elevated hover:text-text-primary"
          >
            {t}
          </Chip>
        </button>
      ))}
      {hidden > 0 && <Chip variant="soft">+{hidden}</Chip>}
    </div>
  )
}

// Фильтры по хабу за одной кнопкой: выложенные в ряд селекты занимали всю
// ширину над лентой и читались как форма, хотя нужны эпизодически. Что именно
// сужает выдачу, видно и с закрытой панелью — счётчик на кнопке и чипы рядом.
// Проект зависит от выбранной компании — тот же инвариант, что в форме звонка;
// при сбросе компании фильтр по проекту снимается вызывающим.
function MeetingFilters({
  company,
  project,
  customerVisible,
  activeCount,
  onCompany,
  onProject,
  onCustomerVisible,
  onReset,
}: {
  company?: string | undefined
  project?: string | undefined
  customerVisible?: true | undefined
  activeCount: number
  onCompany: (id: string | undefined) => void
  onProject: (id: string | undefined) => void
  onCustomerVisible: (value: boolean) => void
  onReset: () => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const {
    anchorRef: triggerRef,
    popoverRef: panelRef,
    popoverProps,
  } = useAnchoredPopover<HTMLButtonElement, HTMLDivElement>({ open })
  const panelId = useId()
  const companies = useQuery(companiesQueryOptions())
  const projects = useQuery({ ...projectsQueryOptions(company), enabled: company !== undefined })

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      // Список селекта живёт в top-layer, но в DOM остаётся внутри панели —
      // contains() по корню накрывает и его.
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Escape при раскрытом списке селекта закрывает список, а не всю панель.
      if (panelRef.current?.querySelector('[role="combobox"][aria-expanded="true"]')) return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, panelRef, triggerRef])

  return (
    <div ref={rootRef} className="inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        {...(open ? { 'aria-controls': panelId } : {})}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-control border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
          activeCount > 0 || open
            ? 'border-border-accent bg-bg-elevated text-text-primary'
            : 'border-border-default text-text-secondary hover:border-border-strong hover:text-text-primary',
        )}
      >
        <Icon name="filter" size={16} />
        Фильтры
        {activeCount > 0 && (
          <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent px-1 py-0.5 font-mono text-[11px] leading-none text-text-inverse">
            {activeCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          {...popoverProps}
          role="dialog"
          aria-label="Фильтры встреч"
          id={panelId}
          className="m-0 w-[min(20rem,calc(100vw-2rem))] rounded-card border border-border-default bg-bg-surface p-4 shadow-lg shadow-black/40"
        >
          <div className="flex flex-col gap-4">
            <Field label="Компания">
              {(props) => (
                <Select
                  {...props}
                  value={company ?? ''}
                  disabled={companies.isPending}
                  placeholder="Все компании"
                  options={(companies.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
                  onChange={(id) => onCompany(id || undefined)}
                />
              )}
            </Field>
            <Field label="Проект">
              {(props) => (
                <Select
                  {...props}
                  value={project ?? ''}
                  disabled={!company || projects.isPending}
                  placeholder={company ? 'Все проекты' : 'Сначала выберите компанию'}
                  options={(company ? (projects.data ?? []) : []).map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                  onChange={(id) => onProject(id || undefined)}
                />
              )}
            </Field>
            {/* Тумблер, а не селект: у видимости клиенту два состояния, и второе
                («скрытые от клиента») отдельным фильтром не спрашивают. */}
            <button
              type="button"
              aria-pressed={customerVisible === true}
              onClick={() => onCustomerVisible(customerVisible !== true)}
              className={cn(
                'inline-flex h-9 items-center gap-2 self-start rounded-control border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
                customerVisible
                  ? 'border-border-accent bg-bg-elevated text-text-primary'
                  : 'border-border-default text-text-secondary hover:border-border-strong hover:text-text-primary',
              )}
            >
              <Icon name="eye" size={16} />
              Видно заказчику
            </button>
            <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-3">
              <button
                type="button"
                disabled={activeCount === 0}
                onClick={onReset}
                className="text-sm text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:opacity-40 disabled:hover:text-text-secondary"
              >
                Сбросить
              </button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setOpen(false)
                  triggerRef.current?.focus()
                }}
              >
                Готово
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Замок на карточке: вход в такую встречу — заявка в лобби, а не мгновенный вход.
function LockedMark() {
  return (
    <span title="Звонок закрыт: вход по допуску" className="shrink-0 text-text-tertiary">
      <Icon name="lock" size={14} />
      <span className="sr-only">Звонок закрыт</span>
    </span>
  )
}

// Приватная встреча: видна только своему кругу — метка объясняет, почему её нет
// у коллег.
function PrivateMark() {
  return (
    <span title="Видна только кругу встречи" className="shrink-0 text-text-tertiary">
      <Icon name="eye-off" size={14} />
      <span className="sr-only">Приватная встреча</span>
    </span>
  )
}

function FilterChip({
  label,
  clearLabel,
  onClear,
}: {
  label: string
  clearLabel: string
  onClear: () => void
}) {
  return (
    <Chip variant="soft">
      {label}
      <button
        type="button"
        aria-label={clearLabel}
        className="text-text-tertiary transition-colors hover:text-text-primary"
        onClick={onClear}
      >
        <Icon name="close" size={12} />
      </button>
    </Chip>
  )
}
